import { HomeCareLeadId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { handleEvent } from "util/user-events";
import { AiSummaryState } from "./ai-summary-state";
import { useEntityAiSummary } from "./use-entity-ai-summary";
import { useHomeCareLeadAiSummaryAccessQuery } from "./use-home-care-lead-ai-summary-access";

export const useHomeCareLeadAiSummary = (
    homeCareLeadId: HomeCareLeadId | null,
): AiSummaryState => {
    const trpcClient = trpc.useTRPC();

    const accessQuery = useHomeCareLeadAiSummaryAccessQuery(homeCareLeadId);
    const generateMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.generate.mutationOptions(),
    });
    const submitFeedbackMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.submitFeedback.mutationOptions(),
    });
    const updateTextMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.updateText.mutationOptions(),
    });
    const removeSummaryMutation = useMutation({
        ...trpcClient.homeCareLeadAiSummary.removeSummary.mutationOptions(),
    });

    return useEntityAiSummary({
        summary: accessQuery.data?.summary,
        isQueryLoading: accessQuery.isLoading,
        queryError: accessQuery.error?.message,
        refetchSummary: () => accessQuery.refetch(),

        triggerGenerate: () => {
            if (!homeCareLeadId) {
                return Promise.reject(
                    new Error(
                        "Cannot generate a home care lead summary without a home care lead id",
                    ),
                );
            }
            return generateMutation.mutateAsync({ entityId: homeCareLeadId });
        },
        isGenerating: generateMutation.isPending,
        generateError: generateMutation.error?.message,

        triggerSubmitFeedback: input => submitFeedbackMutation.mutate(input),
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        submitFeedbackError: submitFeedbackMutation.error?.message,

        triggerUpdateText: input => updateTextMutation.mutateAsync(input),
        updateTextError: updateTextMutation.error?.message,

        triggerRemoveSummary: input => removeSummaryMutation.mutateAsync(input),
        isRemoving: removeSummaryMutation.isPending,
        removeSummaryError: removeSummaryMutation.error?.message,

        trackGenerate: () =>
            handleEvent("ai-summary-generate", { homeCareLeadId, isRegenerate: false }),
        trackRegenerate: () =>
            handleEvent("ai-summary-generate", { homeCareLeadId, isRegenerate: true }),
    });
};
